import React from 'react';
import { Shield, AlertTriangle, RefreshCw, X } from 'lucide-react';
import { Button } from './Button';
import { cn } from '../../lib/utils';

interface ErrorBannerProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  className?: string;
  variant?: 'danger' | 'warning';
}

export const ErrorBanner: React.FC<ErrorBannerProps> = ({
  title = 'Something went wrong',
  message,
  onRetry,
  className,
  variant = 'danger'
}) => {
  const isDanger = variant === 'danger';

  return (
    <div className={cn(
      "rounded-2xl border p-4 animate-in fade-in slide-in-from-top-2 duration-300",
      isDanger 
        ? "bg-red-50 border-red-200 text-red-900" 
        : "bg-amber-50 border-amber-200 text-amber-900",
      className
    )}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={cn(
            "p-2 rounded-xl",
            isDanger ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"
          )}>
            {isDanger ? <Shield size={20} /> : <AlertTriangle size={20} />}
          </div>
          <div>
            <p className="text-sm font-bold leading-tight">{title}</p>
            {message && <p className={cn(
              "text-xs mt-0.5",
              isDanger ? "text-red-700" : "text-amber-800"
            )}>{message}</p>}
          </div>
        </div>
        {onRetry && (
          <Button 
            size="sm" 
            onClick={onRetry} 
            variant="primary" 
            className={cn(
              "h-8 text-xs gap-1.5 shrink-0",
              isDanger 
                ? "bg-red-600 hover:bg-red-700 border-none px-3" 
                : "bg-amber-600 hover:bg-amber-700 border-none px-3"
            )}
          >
            <RefreshCw size={14} className="animate-spin-once" />
            Retry
          </Button>
        )}
      </div>
    </div>
  );
};
